import Icon from '../components/Icon';
import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GameContext } from '../components/GameContext';
import { generateUniqueGameCode, checkIfGameCodeExists } from '../components/pre_game/GameCodeUtils';
import { addPlayerData } from '../components/utils/dbUtils';
import { allPrompts, packInfo, readSaved, save } from '../gameDeck';
import BrandMark from '../components/BrandMark';
import { ACTIVE_ROOM, PENDING_TV } from '../creativeDisplay';

const sampleCard = allPrompts.find(card => card.description.includes('you are still owed an apology for a board-game incident.'));

export default function LandingPage() {
  const { setGameCode, setPlayerId, playAgainResetContext } = useContext(GameContext);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const session = readSaved('diyt-session-v2', null);
  async function enterRoom(host) {
    if (busy) return;
    setBusy(host ? 'host' : 'join'); setError('');
    try {
      const room = host ? await generateUniqueGameCode() : code;
      if (!host) {
        const exists = await checkIfGameCodeExists(room);
        if (!exists.exists || exists.expiredCode) throw new Error(exists.expiredCode ? 'That room has expired. Ask your host for a new code.' : 'No room with that code. Double-check the four digits.');
      }
      let stored = readSaved(room + '_data', null);
      if (host || !stored?.playerId) {
        const id = await addPlayerData(room, { name: '', isAddingPrompts: true, isReady: false, isHost: host, prompts: [] });
        stored = { gameId: room, isHost: host, playerId: id, creationTimestamp: new Date().toISOString() };
        save(room + '_data', stored);
      }
      playAgainResetContext(); setGameCode(room); setPlayerId(stored.playerId);
      const next = host ? 'share' : stored.isHost && stored.orderOfPrompts ? 'play' : 'enter-prompts';
      if (stored.isHost) save(ACTIVE_ROOM, { code: room, route: next });
      if (host && readSaved(PENDING_TV, '')) { navigate(`/creative-mode/connect?room=${room}`); return; }
      navigate(`/creative-mode/${room}/${next}`);
    } catch (e) { setError(e.code ? 'We couldn’t connect to the game room. Check your connection and try again. Simple mode works without a room.' : e.message); }
    finally { setBusy(''); }
  }
  return <div className="home">
    <section className="game-box" aria-label="Start a game">
      <div className="box-cover">
        <h1><span className="sr-only">Drink If You Think</span><BrandMark /></h1>
        <p className="box-copy">Good company.<br />Questionable opinions.</p>
        <Link className="button box-play" to="/simple-mode/personalize">Let’s play <Icon name="arrow-right" /></Link>
        <div className="box-smallprint"><span>2+ people. One phone.</span><a href="#how-to-play">How to play</a></div>
        {session?.deck?.length > 0 && !session.finished && <Link className="resume-link" to="/simple-mode/play">Resume your round <span>Card {session.index + 1} of {session.deck.length} <Icon name="arrow-right" /></span></Link>}
      </div>
      <div className="sample-deck">
        <article className="sample-card" aria-label="A card from The group chat pack">
          <div className="sample-card-top"><span>THE GROUP CHAT</span><Icon name="people" /></div>
          <div className="sample-card-prompt"><p>Drink if you think…</p><h2>{sampleCard.description.replace(/^Drink if (?:you think\s*)?/i, '')}</h2></div>
          <div className="sample-card-bottom"><span>EYES CLOSED.<br />HONEST ANSWERS.</span><span className="sample-monogram" aria-hidden="true">DIYT</span></div>
        </article>
        <p className="sample-caption">Go on. Look them in the eye.</p>
      </div>
    </section>
    <section className="room-table" id="creative" aria-labelledby="room-heading">
      <div className="room-invitation"><h2 id="room-heading">Got inside jokes?</h2><p>Write your own secret prompts together. Then guess who wrote what.</p><button className="room-create" disabled={!!busy} onClick={() => enterRoom(true)}>{busy === 'host' ? 'Creating your room…' : 'Create a room'} <Icon name="arrow-right" /></button></div>
      <form className="room-join" onSubmit={e => { e.preventDefault(); enterRoom(false); }}>
        <label htmlFor="room-code">Already have a room code?</label>
        <div><input id="room-code" value={code} placeholder="4-digit code" inputMode="numeric" pattern="[0-9]{4}" maxLength={4} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))} required /><button className="button dark" disabled={code.length !== 4 || !!busy}>{busy === 'join' ? 'Joining…' : 'Join'}</button></div>
        <p>Creative mode · 4+ players · Everyone’s phone</p>
        <Link className="room-tv-entry" to="/creative-mode/tv">Play on a TV <Icon name="arrow-right" /></Link>
      </form>
      {error && <p role="alert" className="error-message">{error}</p>}
    </section>
    <section className="how-section" id="how-to-play">
      <div className="section-heading"><span className="eyebrow">HOW TO PLAY</span><h2>Same answer?<br />Double trouble.</h2></div>
      <div className="how-steps">
        <div><span className="step-number">01</span><h3>Pair up.</h3><p>Pick two players. Read the card out loud, then both close your eyes.</p></div>
        <div><span className="step-number">02</span><h3>Be honest.</h3><p>If you agree with the prompt, take a sip. Don’t agree? Sit this one out.</p></div>
        <div><span className="step-number">03</span><h3>Compare notes.</h3><p>Both sip or neither sip? That’s double trouble: both take a sip. Switch pairs and go again.</p></div>
      </div>
      <p className="gentle-note">Your drink, your choice. Raise a hand instead, skip any card, and play at your own pace.</p>
    </section>
    <section className="pack-preview" aria-labelledby="packs-heading"><h2 id="packs-heading">Pick your company.</h2><div>{packInfo.map((pack, index) => <Link key={pack.id} to={`/simple-mode/personalize?pack=${encodeURIComponent(pack.id)}`} className="pack-ticket"><span className="ticket-number" aria-hidden="true">0{index + 1}</span><span><strong>{pack.name}</strong><span>{pack.detail}</span></span><Icon name="arrow-right" /></Link>)}</div></section>
  </div>;
}
